import { bridgeRequest, httpRequest, makeRequestURL, REACT_QUERY_KEYS } from 'common'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { TEndpoint, TCountry, TEntity } from 'contexts/app-context/types'
import { useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { entityPayloadMap } from 'contexts/app-context/types/app-context/components/utils'
import { logBridgeError, sendScreenView } from 'common/helpers'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  TPreparedTicketFile,
  TSendReportRequest,
  TSendReportResponse,
  TUpdateReportRequest,
  TUploadUrlRequest,
  TUploadUrlResponse,
} from '../types/NewService.types'

const makeOrganizationHeaders = ({
  userCountry,
  userSelectedEntity,
}: {
  userCountry?: TCountry
  userSelectedEntity?: TEntity
}) => {
  if (!userCountry || !userSelectedEntity) {
    return undefined
  }

  return {
    'x-inter-user-org': userCountry,
    'x-inter-account-org': entityPayloadMap[userSelectedEntity],
  }
}

const sendNewServiceDurationEvent = async (flow: string, step: string, scenario: string) => {
  try {
    await sendScreenView({
      flow,
      step,
      scenario,
      screen_name: Routes.NEW_SERVICE,
      event_name: 'async_event',
    })
  } catch (error: unknown) {
    logBridgeError(`send ${step} duration event`, error)
  }
}

export const uploadTicketFile = async ({ file, uploadUrl, type }: TPreparedTicketFile) =>
  httpRequest<void>({
    method: 'PUT',
    url: uploadUrl,
    headers: {
      'Content-Type': type,
    },
    data: file,
  })

export const useGetUploadUrls = () => {
  const { isUSBranch, userCountry, userSelectedEntity } = useAppContext()

  return useMutation({
    mutationFn: ({ endpoint, externalId, files }: TUploadUrlRequest & { endpoint: TEndpoint }) =>
      bridgeRequest<TUploadUrlResponse>({
        endpoint: {
          ...endpoint,
          headers: {
            ...endpoint.headers,
            ...makeOrganizationHeaders({ userCountry, userSelectedEntity }),
          },
        },
        body: {
          ...(externalId ? { externalId } : {}),
          files,
        },
        returnAllResponse: false,
        isUSBranch,
      }),
  })
}

export const useUpdateRequest = ({ onFinally }: { onFinally: () => void }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isUSBranch, userCountry, userSelectedEntity } = useAppContext()

  const mutation = useMutation({
    mutationFn: ({ protocol, ticketFiles, endpoint }: TUpdateReportRequest) => {
      const startedAt = performance.now()

      return bridgeRequest<TSendReportResponse>({
        endpoint: {
          ...endpoint,
          url: makeRequestURL(endpoint.url, { protocol }),
          headers: {
            ...endpoint.headers,
            ...makeOrganizationHeaders({ userCountry, userSelectedEntity }),
          },
        },
        body: ticketFiles,
        returnAllResponse: false,
        isUSBranch,
      }).finally(() => {
        const endedAt = performance.now()
        const requestDuration = endedAt - startedAt

        sendNewServiceDurationEvent(
          'update_request',
          'update_request',
          JSON.stringify({
            isUSBranch,
            protocol,
            endpoint,
            userSelectedEntity,
            userCountry,
            startedAt,
            endedAt,
            requestDuration,
          }),
        )
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SUPPORT] })

      setTimeout(() => {
        navigate(Routes.SUCCES_SENDING_REPORT)
        if (onFinally) onFinally()
      }, 1000)
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SUPPORT] })

      setTimeout(() => {
        navigate(Routes.ERROR_SENDING_REPORT)
        if (onFinally) onFinally()
      }, 1000)
    },
  })

  return {
    ...mutation,
  }
}

export const useSendRequest = ({ onFinally }: { onFinally: () => void }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isUSBranch, userSelectedEntity, userCountry } = useAppContext()

  const mutation = useMutation({
    mutationFn: ({
      newResquestData,
      endpoint,
    }: {
      newResquestData: TSendReportRequest
      endpoint: TEndpoint
    }) => {
      const startedAt = performance.now()

      return bridgeRequest<TSendReportResponse>({
        endpoint: {
          ...endpoint,
          headers: {
            ...endpoint.headers,
            ...makeOrganizationHeaders({ userCountry, userSelectedEntity }),
          },
        },
        body: newResquestData,
        returnAllResponse: false,
        isUSBranch,
      }).finally(() => {
        const endedAt = performance.now()
        const requestDuration = endedAt - startedAt

        sendNewServiceDurationEvent(
          'create_request',
          'create_request',
          JSON.stringify({
            isUSBranch,
            endpoint,
            userSelectedEntity,
            userCountry,
            startedAt,
            endedAt,
            requestDuration,
          }),
        )
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SUPPORT] })

      setTimeout(() => {
        navigate(Routes.SUCCES_SENDING_REPORT)
        if (onFinally) onFinally()
      }, 1000)
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SUPPORT] })

      setTimeout(() => {
        navigate(Routes.ERROR_SENDING_REPORT)
        if (onFinally) onFinally()
      }, 1000)
    },
  })

  return {
    ...mutation,
  }
}
